import { ControllerFontType, makeStyles } from '@bymason-private/controller-v1-theme-provider';
import { ArtifactType, ConfigModel, GetArtifactsOptionsList } from '@bymason/controller-api-hooks';
import { ConfigReview } from '../../../../common/config-review';
import { ControllerAlert, Link, Text } from '@bymason-private/legacy-pattern-components';
import { RouterLink } from '@bymason-private/route-provider';
import { routes } from '../../../../router';
import { useMemo } from 'react';

const useStyles = makeStyles(() => ({
  root: {
    margin: 0,
  },
  heading: {
    marginBottom: 16,
  },
  code: {
    margin: 0,
    marginTop: 12,
    marginBottom: 12,
    '& fieldset': {
      borderWidth: 0,
    },
  },
}));

export default function ReviewStep({ config, invalidArtifacts, onInvalidArtifacts }: ReviewStepProps) {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.heading}>
        <Text variant={ControllerFontType.Heading300B}>Review your configuration.{` `}</Text>
        <Text variant={ControllerFontType.Body400R}>
          Make sure that app and media names and versions in the config match what was selected, and review
          your chosen configurations before starting a build.
        </Text>
      </div>
      {invalidArtifacts && invalidArtifacts.length > 0 && (
        <MissingArtifactsAlert projectName={config.os.name} invalidArtifacts={invalidArtifacts} />
      )}
      <ConfigReview config={config} projectName={config.os.name} onInvalidArtifacts={onInvalidArtifacts} />
    </div>
  );
}

interface ReviewStepProps {
  config: ConfigModel;
  invalidArtifacts?: GetArtifactsOptionsList;
  onInvalidArtifacts?: (invalidArtifacts: GetArtifactsOptionsList) => void;
}

// Alert components
const MissingArtifactsAlert = ({
  projectName,
  invalidArtifacts,
}: {
  projectName: string;
  invalidArtifacts: GetArtifactsOptionsList;
}) => {
  const hasInvalidApk = useMemo(() => {
    return invalidArtifacts.find((art) => art.type === ArtifactType.Apk) ? true : false;
  }, [invalidArtifacts]);

  const hasInvalidMedia = useMemo(() => {
    return invalidArtifacts.find((art) => art.type === ArtifactType.Media) ? true : false;
  }, [invalidArtifacts]);

  return (
    <ControllerAlert
      sx={{
        width: '100%',
        marginBottom: '8px',
      }}
      variant='outlined'
      severity='error'
    >
      <div>
        <span>
          {`Please verify all ${
            hasInvalidApk && hasInvalidMedia ? 'apps and media files' : hasInvalidApk ? 'apps' : 'media files'
          } in your configuration have been uploaded and selected in the `}
          <RouterLink
            linkComponent={
              <Link target='_blank' rel='noopener noreferrer'>
                previous steps
              </Link>
            }
            route={routes.ProjectBuildStepper}
            state={{ stepId: hasInvalidApk ? 'apps' : 'media', name: projectName }}
          />
        </span>
      </div>
    </ControllerAlert>
  );
};
